import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useProfileStyles from './profileStyles';
import GenericState from '../../MyComponents/GenericLoadingErrorState/GenericState';
import userApi from '../../Api/userApi';
import { cx } from '../Home/HomeDashboard/dashboardStyles';

/** Links (or unlinks) the account's Digitaliada login. */
const AccountLinking = () => {
    const classes = useProfileStyles();
    const intl = useIntl();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [linkedAccount, setLinkedAccount] = useState(null);
    const [digitaliadaEmail, setDigitaliadaEmail] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        userApi
            .getLinkedAccount()
            .then((response) => setLinkedAccount(response.data.linked ? response.data.linkedAccount : null))
            .catch((err) => console.error('Failed to fetch linked account', err))
            .finally(() => setLoading(false));
    }, []);

    const errorMessage = (err) => {
        const code = err.response?.data?.error;
        if (code) {
            const translated = intl.formatMessage({ id: `accountLinking.error.${code}`, defaultMessage: '' });
            if (translated) return translated;
        }
        return err.response?.data?.message || intl.formatMessage({ id: 'accountLinking.error.unexpected' });
    };

    const link = (event) => {
        event.preventDefault();
        if (!digitaliadaEmail.trim() || submitting) return;
        setSubmitting(true);
        setError(null);
        setSuccess(null);
        userApi
            .linkDigitaliadaAccount(digitaliadaEmail)
            .then((response) => {
                setLinkedAccount(response.data.linkedAccount);
                setDigitaliadaEmail('');
                setSuccess(intl.formatMessage({ id: 'accountLinking.success.linked' }));
            })
            .catch((err) => setError(errorMessage(err)))
            .finally(() => setSubmitting(false));
    };

    const unlink = () => {
        setSubmitting(true);
        setError(null);
        setSuccess(null);
        userApi
            .unlinkDigitaliadaAccount()
            .then(() => {
                setLinkedAccount(null);
                setSuccess(intl.formatMessage({ id: 'accountLinking.success.unlinked' }));
            })
            .catch((err) => setError(errorMessage(err)))
            .finally(() => setSubmitting(false));
    };

    return (
        <form className={classes.card} onSubmit={link}>
            <h2 className={classes.cardTitle}>
                <FormattedMessage id="accountLinking.title" />
            </h2>
            <p className={classes.text}>
                <FormattedMessage id="accountLinking.description" />
            </p>

            {error ? (
                <div className={cx(classes.notice, classes.noticeError)} role="alert">
                    {error}
                </div>
            ) : null}
            {success ? (
                <div className={cx(classes.notice, classes.noticeOk)} role="status">
                    {success}
                </div>
            ) : null}

            {loading ? (
                <GenericState isLoading={true} inline={true} />
            ) : linkedAccount ? (
                <>
                    <div className={cx(classes.notice, classes.noticeOk)}>
                        <FormattedMessage
                            id="accountLinking.linked.info"
                            values={{ email: linkedAccount.digitaliadaEmail }}
                        />
                        <br />
                        <span className={classes.note}>
                            <FormattedMessage
                                id="accountLinking.linked.date"
                                values={{ date: intl.formatDate(linkedAccount.linkedAt) }}
                            />
                        </span>
                    </div>
                    <div className={classes.actions}>
                        <button type="button" onClick={unlink} disabled={submitting} className={classes.danger}>
                            <FormattedMessage id="accountLinking.button.unlink" />
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <label className={classes.field} style={{ maxWidth: '420px', marginBottom: '16px' }}>
                        <span className={classes.label}>{intl.formatMessage({ id: 'accountLinking.input.label' })}</span>
                        <input
                            type="text"
                            value={digitaliadaEmail}
                            placeholder={intl.formatMessage({ id: 'accountLinking.input.placeholder' })}
                            onChange={(event) => setDigitaliadaEmail(event.target.value)}
                            disabled={submitting}
                            className={classes.input}
                        />
                        <span className={classes.note}>{intl.formatMessage({ id: 'accountLinking.input.helper' })}</span>
                    </label>
                    <div className={classes.actions}>
                        <button
                            type="submit"
                            disabled={!digitaliadaEmail.trim() || submitting}
                            className={classes.primary}
                        >
                            <FormattedMessage id="accountLinking.button.link" />
                            <span aria-hidden="true">→</span>
                        </button>
                    </div>
                </>
            )}
        </form>
    );
};

export default AccountLinking;
